/**
 * Admin AI Models Page
 * Manage AI providers and models via Firebase
 * Changes here sync automatically to the IDE
 */

import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  Bot,
  Brain,
  Sparkles,
  Zap,
  Server,
  Wind,
  Cloud,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Star,
  Database,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getAIProviders,
  getAIModels,
  addAIProvider,
  addAIModel,
  updateAIProvider,
  updateAIModel,
  deleteAIProvider,
  deleteAIModel,
  toggleAIProvider,
  toggleAIModel,
  setDefaultAIModel,
  seedAIProvidersAndModels,
  onAIProvidersChange,
  onAIModelsChange,
  type FirestoreAIProvider,
  type FirestoreAIModel,
  type AIProviderType,
} from '@/services/firebase/adminFirestore';
import { isFirebaseConfigured } from '@/services/firebase/config';

// Provider icons
const PROVIDER_ICONS: Record<AIProviderType, React.ReactNode> = {
  openai: <Bot size={20} className="text-green-500" />,
  anthropic: <Brain size={20} className="text-orange-500" />,
  google: <Sparkles size={20} className="text-blue-500" />,
  xai: <Zap size={20} className="text-purple-500" />,
  mistral: <Wind size={20} className="text-cyan-500" />,
  ollama: <Server size={20} className="text-gray-500" />,
  cohere: <Cloud size={20} className="text-pink-500" />,
};

const PROVIDER_COLORS: Record<AIProviderType, string> = {
  openai: 'bg-green-500/10 border-green-500/30',
  anthropic: 'bg-orange-500/10 border-orange-500/30',
  google: 'bg-blue-500/10 border-blue-500/30',
  xai: 'bg-purple-500/10 border-purple-500/30',
  mistral: 'bg-cyan-500/10 border-cyan-500/30',
  ollama: 'bg-gray-500/10 border-gray-500/30',
  cohere: 'bg-pink-500/10 border-pink-500/30',
};

// LocalStorage cache keys
const CACHE_KEYS = {
  PROVIDERS: 'ochtopus_ai_providers_cache',
  MODELS: 'ochtopus_ai_models_cache',
};

// Load from localStorage cache
function loadFromCache<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 5 minutes
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Cache load error:', e);
  }
  return null;
}

// Save to localStorage cache
function saveToCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) {
    console.warn('Cache save error:', e);
  }
}

export function AdminAIModels() {
  // Initialize from cache for instant display
  const [providers, setProviders] = useState<FirestoreAIProvider[]>(() =>
    loadFromCache<FirestoreAIProvider[]>(CACHE_KEYS.PROVIDERS) || []
  );
  const [models, setModels] = useState<FirestoreAIModel[]>(() =>
    loadFromCache<FirestoreAIModel[]>(CACHE_KEYS.MODELS) || []
  );
  const [isLoading, setIsLoading] = useState(() => {
    // Only show loading if no cache
    return !loadFromCache(CACHE_KEYS.PROVIDERS);
  });
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [isAddingModel, setIsAddingModel] = useState<AIProviderType | null>(null);
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);

  // New model form
  const [newModel, setNewModel] = useState({
    name: '',
    displayName: '',
    description: '',
    contextLength: 128000,
    maxTokens: 4096,
    inputPrice: 0,
    outputPrice: 0,
    capabilities: ['chat', 'code'],
  });

  // Set up real-time listeners (no initial loadData call - listeners will provide data)
  useEffect(() => {
    // Real-time listeners update UI automatically
    const unsubProviders = onAIProvidersChange((newProviders) => {
      setProviders(newProviders);
      saveToCache(CACHE_KEYS.PROVIDERS, newProviders);
      setIsLoading(false);
    });

    const unsubModels = onAIModelsChange((newModels) => {
      setModels(newModels);
      saveToCache(CACHE_KEYS.MODELS, newModels);
    });

    return () => {
      unsubProviders();
      unsubModels();
    };
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [providersData, modelsData] = await Promise.all([
        getAIProviders(),
        getAIModels(),
      ]);
      setProviders(providersData);
      setModels(modelsData);
      // Update cache
      saveToCache(CACHE_KEYS.PROVIDERS, providersData);
      saveToCache(CACHE_KEYS.MODELS, modelsData);
    } catch (error) {
      console.error('Failed to load AI data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will add default AI providers and models. Continue?')) return;

    setIsSeeding(true);
    try {
      await seedAIProvidersAndModels();
      await loadData();
    } catch (error) {
      console.error('Failed to seed data:', error);
      alert('Failed to seed data: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsSeeding(false);
    }
  };

  const handleToggleProvider = async (providerId: string, enabled: boolean) => {
    try {
      await toggleAIProvider(providerId, enabled);
    } catch (error) {
      console.error('Failed to toggle provider:', error);
    }
  };

  const handleToggleModel = async (modelId: string, enabled: boolean) => {
    try {
      await toggleAIModel(modelId, enabled);
    } catch (error) {
      console.error('Failed to toggle model:', error);
    }
  };

  const handleSetDefault = async (providerId: AIProviderType, modelId: string) => {
    try {
      await setDefaultAIModel(providerId, modelId);
    } catch (error) {
      console.error('Failed to set default model:', error);
    }
  };

  const handleAddModel = async (providerId: AIProviderType) => {
    if (!newModel.name || !newModel.displayName) {
      alert('Name and Display Name are required');
      return;
    }

    try {
      const providerModels = models.filter(m => m.providerId === providerId);
      await addAIModel({
        providerId,
        name: newModel.name,
        displayName: newModel.displayName,
        description: newModel.description,
        contextLength: newModel.contextLength,
        maxTokens: newModel.maxTokens,
        inputPrice: newModel.inputPrice,
        outputPrice: newModel.outputPrice,
        capabilities: newModel.capabilities,
        enabled: true,
        isDefault: providerModels.length === 0,
        order: providerModels.length + 1,
      });

      setIsAddingModel(null);
      setNewModel({
        name: '',
        displayName: '',
        description: '',
        contextLength: 128000,
        maxTokens: 4096,
        inputPrice: 0,
        outputPrice: 0,
        capabilities: ['chat', 'code'],
      });
    } catch (error) {
      console.error('Failed to add model:', error);
      alert('Failed to add model: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!confirm('Are you sure you want to delete this model?')) return;

    try {
      await deleteAIModel(modelId);
    } catch (error) {
      console.error('Failed to delete model:', error);
    }
  };

  const getModelsForProvider = (providerId: AIProviderType) => {
    return models.filter(m => m.providerId === providerId);
  };

  // Stats
  const enabledProviders = providers.filter(p => p.enabled).length;
  const totalModels = models.length;
  const enabledModels = models.filter(m => m.enabled).length;

  if (!isFirebaseConfigured()) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Firebase Not Configured</h2>
        <p className="text-text-secondary max-w-md">
          Firebase is required for AI Models management. Please configure Firebase in your environment.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">AI Models</h1>
          <p className="text-text-secondary">
            Manage AI providers and models. Changes sync automatically to the IDE.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {providers.length === 0 && (
            <button
              onClick={handleSeedData}
              disabled={isSeeding}
              className="btn bg-accent text-white hover:bg-accent/90"
            >
              {isSeeding ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database size={16} />
                  Seed Default Data
                </>
              )}
            </button>
          )}
          <button
            onClick={loadData}
            disabled={isLoading}
            className="btn btn-secondary"
          >
            <RefreshCw size={16} className={cn(isLoading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Server className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{enabledProviders}/{providers.length}</p>
              <p className="text-sm text-text-secondary">Enabled Providers</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Bot className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{enabledModels}/{totalModels}</p>
              <p className="text-sm text-text-secondary">Enabled Models</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Cloud className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">Firebase</p>
              <p className="text-sm text-text-secondary">Real-time Sync</p>
            </div>
          </div>
        </div>
      </div>

      {/* Provider Cards */}
      {providers.length === 0 ? (
        <div className="bg-bg-card border border-border-default rounded-xl p-8 text-center">
          <Database className="w-12 h-12 mx-auto mb-4 text-text-tertiary" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Providers Found</h3>
          <p className="text-text-secondary mb-4">
            Click "Seed Default Data" to add the default AI providers and models.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {providers.map((provider) => {
            const providerModels = getModelsForProvider(provider.type);
            const isExpanded = expandedProvider === provider.id;

            return (
              <div
                key={provider.id}
                className={cn(
                  'bg-bg-card border rounded-xl overflow-hidden',
                  PROVIDER_COLORS[provider.type] || 'border-border-default'
                )}
              >
                {/* Provider Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedProvider(isExpanded ? null : provider.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center">
                      {PROVIDER_ICONS[provider.type]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">{provider.name}</h3>
                      <p className="text-xs text-text-tertiary">
                        {providerModels.length} model{providerModels.length !== 1 ? 's' : ''} •
                        {providerModels.filter(m => m.enabled).length} enabled
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={provider.enabled}
                        onChange={(e) => handleToggleProvider(provider.id, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className={cn(
                        "w-11 h-6 rounded-full peer transition-colors",
                        "peer-checked:after:translate-x-full peer-checked:after:border-white",
                        "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                        "after:bg-white after:border-gray-300 after:border after:rounded-full",
                        "after:h-5 after:w-5 after:transition-all",
                        provider.enabled ? 'bg-accent' : 'bg-bg-elevated'
                      )} />
                    </label>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Models List */}
                {isExpanded && (
                  <div className="border-t border-border-default p-4 space-y-3">
                    {/* Provider Disabled Warning */}
                    {!provider.enabled && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2 text-yellow-600">
                        <AlertCircle size={16} />
                        <span className="text-sm">Provider is disabled. All models below are inactive in the IDE.</span>
                      </div>
                    )}
                    {providerModels.length === 0 ? (
                      <p className="text-text-tertiary text-sm text-center py-4">
                        No models configured for this provider.
                      </p>
                    ) : (
                      providerModels.map((model) => {
                        // Model is effectively disabled if provider is disabled
                        const isEffectivelyEnabled = provider.enabled && model.enabled;

                        return (
                        <div
                          key={model.id}
                          className={cn(
                            'bg-bg-elevated rounded-lg p-4 border',
                            isEffectivelyEnabled ? 'border-border-default' : 'border-border-default opacity-60'
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-text-primary">{model.displayName}</h4>
                                {model.isDefault && (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10 text-accent flex items-center gap-1">
                                    <Star size={10} />
                                    Default
                                  </span>
                                )}
                              </div>
                              <code className="text-xs text-text-tertiary font-mono mt-1 block">
                                {model.name}
                              </code>
                              <p className="text-sm text-text-secondary mt-1">{model.description}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className="px-2 py-0.5 rounded bg-bg-base text-xs text-text-tertiary">
                                  {(model.contextLength / 1000).toFixed(0)}K context
                                </span>
                                <span className="px-2 py-0.5 rounded bg-bg-base text-xs text-text-tertiary">
                                  ${model.inputPrice}/${model.outputPrice} per 1M tokens
                                </span>
                                {model.capabilities.map(cap => (
                                  <span key={cap} className="px-2 py-0.5 rounded bg-bg-base text-xs text-text-tertiary">
                                    {cap}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!model.isDefault && (
                                <button
                                  onClick={() => handleSetDefault(provider.type, model.id)}
                                  className="p-2 rounded hover:bg-bg-base text-text-tertiary hover:text-accent transition-colors"
                                  title="Set as default"
                                >
                                  <Star size={16} />
                                </button>
                              )}
                              <label className={cn(
                                "relative inline-flex items-center",
                                provider.enabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                              )}>
                                <input
                                  type="checkbox"
                                  checked={isEffectivelyEnabled}
                                  onChange={(e) => handleToggleModel(model.id, e.target.checked)}
                                  disabled={!provider.enabled}
                                  className="sr-only peer"
                                />
                                <div className={cn(
                                  "w-9 h-5 rounded-full peer transition-colors",
                                  "peer-checked:after:translate-x-full peer-checked:after:border-white",
                                  "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                                  "after:bg-white after:border-gray-300 after:border after:rounded-full",
                                  "after:h-4 after:w-4 after:transition-all",
                                  isEffectivelyEnabled ? 'bg-green-500' : 'bg-bg-base'
                                )} />
                              </label>
                              <button
                                onClick={() => handleDeleteModel(model.id)}
                                className="p-2 rounded hover:bg-red-500/10 text-red-500 transition-colors"
                                title="Delete model"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })
                    )}

                    {/* Add Model Form */}
                    {isAddingModel === provider.type ? (
                      <div className="bg-bg-elevated rounded-lg p-4 border border-accent">
                        <h4 className="font-medium text-text-primary mb-4">Add New Model</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Model Name (API)</label>
                            <input
                              type="text"
                              value={newModel.name}
                              onChange={(e) => setNewModel(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="e.g., gpt-4-turbo"
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none font-mono"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Display Name</label>
                            <input
                              type="text"
                              value={newModel.displayName}
                              onChange={(e) => setNewModel(prev => ({ ...prev, displayName: e.target.value }))}
                              placeholder="e.g., GPT-4 Turbo"
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-text-tertiary mb-1 block">Description</label>
                            <input
                              type="text"
                              value={newModel.description}
                              onChange={(e) => setNewModel(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="e.g., Most capable GPT-4 model"
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Context Length</label>
                            <input
                              type="number"
                              value={newModel.contextLength}
                              onChange={(e) => setNewModel(prev => ({ ...prev, contextLength: parseInt(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Max Tokens</label>
                            <input
                              type="number"
                              value={newModel.maxTokens}
                              onChange={(e) => setNewModel(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Input Price ($ per 1M tokens)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={newModel.inputPrice}
                              onChange={(e) => setNewModel(prev => ({ ...prev, inputPrice: parseFloat(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-tertiary mb-1 block">Output Price ($ per 1M tokens)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={newModel.outputPrice}
                              onChange={(e) => setNewModel(prev => ({ ...prev, outputPrice: parseFloat(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => handleAddModel(provider.type)}
                            className="btn btn-primary btn-sm"
                          >
                            <Plus size={14} />
                            Add Model
                          </button>
                          <button
                            onClick={() => setIsAddingModel(null)}
                            className="btn btn-secondary btn-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsAddingModel(provider.type)}
                        className="w-full py-3 border-2 border-dashed border-border-default rounded-lg text-text-tertiary hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus size={16} />
                        Add Model
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-accent/10 border border-accent/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Cloud className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-accent">Real-time Sync</h4>
            <p className="text-sm text-text-secondary mt-1">
              Changes made here are automatically synced to Firebase and will appear in the Ochtopus IDE
              within 5 minutes (or immediately after refresh).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
